import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ChevronRight, Home, Check, Shield } from "lucide-react";
import { PlatformShellHeader, PlatformShellFooter, useSeo } from "./PlatformShell";
import { getSku, ECOSYSTEM } from "../data/catalog";

/* /products/:family/:sku — individual SKU detail page */
export const ProductSku = () => {
  const { family: familySlug, sku: skuCode } = useParams();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const match = getSku(familySlug, skuCode);
  const jsonLd = match
    ? {
        "@context": "https://schema.org/",
        "@type": "Product",
        name: `Boom Barrier Company ${match.sku.name}`,
        sku: match.sku.code,
        brand: { "@type": "Brand", name: "Boom Barrier Company" },
        category: `${match.family.name} ${match.family.wattage}`,
        description: `${match.sku.tagline.replace(/\.$/, "")}. ${match.family.positioning}`,
        image: `https://boombarriercompany.com${match.sku.image}`,
        offers: {
          "@type": "Offer",
          availability: "https://schema.org/InStock",
          url: `https://boombarriercompany.com/products/${familySlug}/${skuCode.toLowerCase()}`,
          priceCurrency: "INR",
          price: "0",
        },
      }
    : null;

  useSeo({
    title: match
      ? `${match.sku.name} · ${match.family.name} ${match.family.wattage} · BBC`
      : "BBC Product SKU",
    description: match
      ? `${match.sku.name} — ${match.sku.tagline.replace(/\.$/, "")}. Cabinet: ${match.sku.cabinet}. Part of the BBC ${match.family.name} family.`
      : "",
    canonical: match
      ? `https://boombarriercompany.com/products/${match.family.slug}/${match.sku.code.toLowerCase()}`
      : undefined,
    jsonLd: jsonLd || undefined,
  });

  if (!match) return <Navigate to="/products" replace />;

  const { family, sku } = match;

  return (
    <div className="bg-[#0F1113] min-h-screen text-white">
      <PlatformShellHeader />
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid={`sku-${sku.code.toLowerCase()}`}>
        <nav className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] flex items-center gap-2 mb-8 flex-wrap" data-testid="sku-breadcrumb">
          <Link to="/" className="hover:text-white flex items-center gap-1"><Home size={11} /> Home</Link>
          <span>/</span>
          <Link to="/products" className="hover:text-white">Products</Link>
          <span>/</span>
          <Link to={`/products/${family.slug}`} className="hover:text-white">{family.name}</Link>
          <span>/</span>
          <span className="text-white">{sku.code}</span>
        </nav>

        {/* Hero */}
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6 relative aspect-[4/3] bg-gradient-to-br from-[#1F2326] via-[#16191C] to-black border border-white/10 overflow-hidden">
            <img src={sku.image} alt={`${sku.name} — ${sku.tagline}`} className="absolute inset-0 w-full h-full object-contain p-8" />
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">{sku.code}</span>
              <span className="font-mono text-[9px] uppercase tracking-wider text-[#00FF85] bg-black/40 backdrop-blur px-2 py-1 border border-white/10">
                <span className="inline-block w-1 h-1 rounded-full bg-[#00FF85] mr-1.5 align-middle" />
                In stock
              </span>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: family.accentColor }}>
              {family.name} · {family.wattage}
            </div>
            <h1 className="mt-3 font-display text-white text-5xl md:text-6xl font-black tracking-[-0.02em] leading-[0.98]" data-testid="sku-heading">
              {sku.code}
            </h1>
            <p className="mt-4 text-[#00FF85] font-mono text-[12px] uppercase tracking-[0.22em]">{sku.tagline}</p>
            <p className="mt-5 text-[#A1A1AA] text-base md:text-lg font-body leading-relaxed">
              {sku.cabinet}
            </p>

            <ul className="mt-8 space-y-3">
              {sku.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-white/85 text-sm font-body">
                  <Check size={16} className="text-[#00FF85] mt-0.5 flex-none" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={`/quote?model=${encodeURIComponent(sku.code)}`}
                data-testid={`sku-cta-${sku.code.toLowerCase()}`}
                className="inline-flex items-center gap-2 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-5 py-3 text-xs uppercase tracking-wider transition shadow-[0_0_20px_rgba(0,255,133,0.3)] hover:shadow-[0_0_30px_rgba(0,255,133,0.5)]"
              >
                Request quote
                <ArrowRight size={14} />
              </Link>
              <Link
                to={`/products/${family.slug}`}
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-white border border-white/15 hover:border-[#00FF85] hover:text-[#00FF85] px-4 py-3 transition"
              >
                Compare {family.name} lineup
                <ChevronRight size={12} />
              </Link>
            </div>
          </div>
        </div>

        {/* Specifications */}
        <div className="mt-24 border-t border-white/5 pt-12" data-testid="sku-specs">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Full specifications</div>
          <h2 className="mt-2 font-display text-white text-3xl md:text-4xl font-black tracking-tight">Engineered mechanics.</h2>

          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {Object.entries(sku.specs).map(([k, v]) => (
              <div key={k} className="border border-white/10 bg-white/[0.02] px-4 py-3">
                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#52525B]">{k}</div>
                <div className="mt-1 font-display text-white text-base font-bold leading-tight">{v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust / Platform */}
        <div className="mt-20 border-t border-white/5 pt-12 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
              <Shield size={12} /> BBC Trust included
            </div>
            <h3 className="mt-2 font-display text-white text-2xl md:text-3xl font-black tracking-tight">
              Every {sku.code} ships with the BBC platform.
            </h3>
            <p className="mt-4 text-[#A1A1AA] text-sm md:text-base font-body leading-relaxed">
              Password-protected controller, AMC cycle-limit lock, remote operations via BBC Connect and a unique QR-verified dispatch record — same across every SKU.
            </p>
            <ul className="mt-6 space-y-2">
              {ECOSYSTEM.smartPlatform.map((f) => (
                <li key={f} className="flex items-start gap-2 text-white/80 text-sm">
                  <Check size={14} className="text-[#00FF85] mt-0.5 flex-none" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Warranty</div>
            <h3 className="mt-2 font-display text-white text-2xl md:text-3xl font-black tracking-tight">Backed end-to-end.</h3>
            <ul className="mt-6 space-y-2">
              {ECOSYSTEM.warranty.map((f) => (
                <li key={f} className="flex items-start gap-2 text-white/80 text-sm">
                  <Check size={14} className="text-[#00FF85] mt-0.5 flex-none" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Related SKUs */}
        <div className="mt-24 border-t border-white/5 pt-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Also in {family.name}</div>
          <div className="mt-6 grid md:grid-cols-3 gap-3">
            {family.skus
              .filter((s) => s.code !== sku.code)
              .slice(0, 3)
              .map((s) => (
                <Link
                  key={s.code}
                  to={`/products/${family.slug}/${s.code.toLowerCase()}`}
                  data-testid={`sku-related-${s.code.toLowerCase()}`}
                  className="group border border-white/10 bg-[#16191C] p-5 hover:border-[#00FF85]/50 transition"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#71717A]">{s.code}</div>
                  <div className="mt-2 font-display text-white text-lg font-bold tracking-tight">{s.name}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: family.accentColor }}>{s.tagline}</div>
                  <div className="mt-3 flex items-center gap-1.5 text-white/60 group-hover:text-[#00FF85] font-mono text-[10px] uppercase tracking-wider transition">
                    View <ChevronRight size={10} />
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </main>
      <PlatformShellFooter />
    </div>
  );
};
